import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
const dur = (d) => (d ? `${Math.round(+d)} s` : "");

function UnverifiedCard({ styles, rule, onChange }) {
  const toggleStyle = (id) => {
    const set = new Set(rule.disabled_styles);
    set.has(id) ? set.delete(id) : set.add(id);
    onChange({ disabled_styles: [...set] });
  };
  return (
    <section className="bg-card border border-border p-6 sm:p-8" data-testid="bank-unverified-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-lg font-bold">Clips non vérifiés</h2>
          <p className="text-xs text-muted-foreground mt-1">{rule.count} clip(s) marqués « unverified » actifs. Désactivés d'un coup ici, ou style par style.</p>
        </div>
        <label className="inline-flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={rule.disabled_all} onChange={(e) => onChange({ disabled_all: e.target.checked })} data-testid="bank-unverified-all" className="h-4 w-4 accent-[#fc1c46]" />
          <span>Masquer partout</span>
        </label>
      </div>
      <div className={`flex flex-wrap gap-2 ${rule.disabled_all ? "opacity-40 pointer-events-none" : ""}`} data-testid="bank-unverified-styles">
        {styles.map((s) => {
          const off = rule.disabled_styles.includes(s.id);
          return (
            <button key={s.id} onClick={() => toggleStyle(s.id)} data-testid={`bank-unverified-style-${s.id}`} aria-pressed={off}
              className={`px-3 py-1.5 text-[11px] font-osd tracking-wider border transition-colors ${off ? "border-primary text-primary line-through" : "border-border text-muted-foreground hover:border-foreground"}`}>
              {s.name} · {rule.per_style?.[s.id] || 0}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ClipRow({ c, first, last, onPatch, onRemove }) {
  return (
    <div className={`flex items-center gap-3 border border-border p-2 ${c.active ? "" : "opacity-50"}`} data-testid={`bank-admin-clip-${c.id}`}>
      <div className="w-12 h-[84px] bg-black shrink-0 overflow-hidden">{c.thumb && <img src={c.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />}</div>
      <div className="flex-1 min-w-0 text-xs">
        <p className="truncate font-bold">{c.title || (c.author_name ? `Pexels · ${c.author_name}` : c.id.slice(0, 8))}</p>
        <p className="text-muted-foreground truncate">{[c.hosting, c.rights, dur(c.duration), c.width && c.height ? `${c.width}×${c.height}` : "", `pos ${c.position}`].filter(Boolean).join(" · ")}</p>
        {c.style_ids?.length > 1 && <p className="text-muted-foreground truncate">aussi dans : {c.style_ids.join(", ")}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onPatch(c, "up")} disabled={first} data-testid={`bank-clip-up-${c.id}`} className="h-9 w-9 border border-border text-sm disabled:opacity-30 hover:border-foreground" aria-label="Monter">↑</button>
        <button onClick={() => onPatch(c, "down")} disabled={last} data-testid={`bank-clip-down-${c.id}`} className="h-9 w-9 border border-border text-sm disabled:opacity-30 hover:border-foreground" aria-label="Descendre">↓</button>
        <button onClick={() => onPatch(c, "toggle")} data-testid={`bank-clip-toggle-${c.id}`} className={`h-9 px-3 border text-[11px] font-osd tracking-wider ${c.active ? "border-border hover:border-foreground" : "border-primary text-primary"}`}>{c.active ? "ACTIF" : "INACTIF"}</button>
        <button onClick={() => onRemove(c)} data-testid={`bank-clip-remove-${c.id}`} className="h-9 px-3 border border-border text-[11px] font-osd tracking-wider text-muted-foreground hover:border-primary hover:text-primary">RETIRER</button>
      </div>
    </div>
  );
}

function PexelsSearch({ styleId, onAdded }) {
  const [q, setQ] = useState("");
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try { const { data } = await api.get("/admin/bank/pexels", { params: { q: q.trim(), orientation: "portrait" } }); setVideos(data.videos || []); }
    catch (e2) { err(e2); } finally { setBusy(false); }
  };
  const add = async (v) => {
    try {
      const { data } = await api.post("/admin/bank/clips", { style_id: styleId, pexels: v });
      toast.success(data.merged ? "Clip déjà présent : ajouté à ce style" : "Clip ajouté au style");
      onAdded();
    } catch (e2) { err(e2); }
  };
  return (
    <section className="bg-card border border-border p-6 sm:p-8" data-testid="bank-pexels-card">
      <h2 className="font-display text-lg font-bold mb-1">Ajouter depuis Pexels</h2>
      <p className="text-xs text-muted-foreground mb-4">Verticaux de préférence, 1080p minimum, 5 à 20 s. Ajouté au style sélectionné à gauche.</p>
      <form onSubmit={search} className="flex gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ex. night city neon" data-testid="bank-pexels-query" className="flex-1 bg-background border border-border px-4 h-11 text-sm focus:border-[#fc1c46] focus:outline-none" />
        <button type="submit" disabled={busy} data-testid="bank-pexels-search" className="bg-primary text-white font-bold px-5 h-11 disabled:opacity-50">Chercher</button>
      </form>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="bank-pexels-results">
        {videos.map((v) => {
          const good = v.height >= 1080 && v.height > v.width && v.duration >= 5 && v.duration <= 20;
          return (
            <button key={v.id} onClick={() => add(v)} data-testid={`bank-pexels-add-${v.id}`} className="group relative aspect-[9/16] bg-black overflow-hidden border border-border hover:border-primary text-left">
              <img src={v.image} alt="" className="w-full h-full object-cover opacity-90 group-hover:opacity-60" loading="lazy" />
              <span className="absolute left-1 top-1 text-[10px] bg-black/70 px-1.5 py-0.5">{Math.round(v.duration)} s · {v.width}×{v.height}</span>
              {!good && <span className="absolute right-1 top-1 text-[10px] bg-[#fc1c46] px-1.5 py-0.5">hors reco</span>}
              <span className="absolute inset-x-0 bottom-0 text-[10px] bg-black/70 px-1.5 py-1 truncate">{v.user?.name}</span>
              <span className="absolute inset-0 grid place-items-center text-sm font-bold opacity-0 group-hover:opacity-100">+ Ajouter</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Reports({ reports, onClose }) {
  if (!reports.length) return null;
  return (
    <section className="bg-card border border-border p-6 sm:p-8" data-testid="bank-reports-card">
      <h2 className="font-display text-lg font-bold mb-4">Signalements ({reports.filter((r) => r.status === "open").length} ouverts)</h2>
      <div className="space-y-2 text-xs">
        {reports.map((r) => (
          <div key={r.report_id} className={`border border-border p-3 flex flex-wrap gap-3 items-start ${r.status === "closed" ? "opacity-50" : ""}`} data-testid={`report-${r.report_id}`}>
            <div className="flex-1 min-w-[200px]">
              <p className="font-bold">{r.kind} · {new Date(r.created_at).toLocaleString("fr-FR")} {r.email ? `· ${r.email}` : ""}</p>
              <p className="break-all text-muted-foreground">{r.target}</p>
              <p className="mt-1">{r.reason}</p>
            </div>
            {r.status === "open" && <button onClick={() => onClose(r)} data-testid={`report-close-${r.report_id}`} className="h-9 px-3 border border-border font-osd text-[11px] tracking-wider hover:border-foreground">TRAITÉ</button>}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function BankAdmin() {
  const [styles, setStyles] = useState(null);
  const [rule, setRule] = useState(null);
  const [styleId, setStyleId] = useState("");
  const [clips, setClips] = useState([]);
  const [total, setTotal] = useState(0);
  const [reports, setReports] = useState([]);

  const loadStyles = useCallback(async () => {
    try {
      const [{ data: s }, { data: u }, { data: r }] = await Promise.all([api.get("/admin/bank/styles"), api.get("/admin/bank/unverified"), api.get("/admin/reports")]);
      setStyles(s.styles); setRule(u); setReports(r.reports || []);
      setStyleId((cur) => cur || s.styles[0]?.id || "");
    } catch (e) { err(e); }
  }, []);
  const loadClips = useCallback(async (more) => {
    if (!styleId) return;
    try {
      const { data } = await api.get("/admin/bank/clips", { params: { style: styleId, offset: more ? clips.length : 0, limit: 60 } });
      setClips((prev) => (more ? [...prev, ...data.clips] : data.clips)); setTotal(data.total);
    } catch (e) { err(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleId, clips.length]);

  useEffect(() => { loadStyles(); }, [loadStyles]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setClips([]); loadClips(false); }, [styleId]);

  const saveRule = async (patch) => {
    try { const { data } = await api.put("/admin/bank/unverified", patch); setRule((r) => ({ ...r, ...data })); toast.success("Règle enregistrée"); loadStyles(); } catch (e) { err(e); }
  };
  const patchClip = async (c, action) => {
    const i = clips.findIndex((x) => x.id === c.id);
    try {
      if (action === "toggle") {
        await api.patch(`/admin/bank/clips/${c.id}`, { active: !c.active });
      } else {
        const j = action === "up" ? i - 1 : i + 1;
        const other = clips[j]; if (!other) return;
        const pa = c.position, pb = other.position === pa ? pa + (action === "up" ? -1 : 1) : other.position;
        await Promise.all([api.patch(`/admin/bank/clips/${c.id}`, { position: pb }), api.patch(`/admin/bank/clips/${other.id}`, { position: pa })]);
      }
      await loadClips(false); loadStyles();
    } catch (e) { err(e); }
  };
  const removeClip = async (c) => {
    if (!window.confirm("Retirer ce clip de la banque ? Il disparaît immédiatement pour tous les artistes (les projets qui l'utilisent afficheront « indisponible »).")) return;
    try { await api.delete(`/admin/bank/clips/${c.id}`); toast.success("Clip retiré"); await loadClips(false); loadStyles(); } catch (e) { err(e); }
  };
  const closeReport = async (r) => {
    try { await api.patch(`/admin/reports/${r.report_id}`, { status: "closed" }); setReports((rs) => rs.map((x) => (x.report_id === r.report_id ? { ...x, status: "closed" } : x))); } catch (e) { err(e); }
  };

  if (!styles || !rule) return <p className="font-osd text-sm text-muted-foreground animate-pulse">CHARGEMENT…</p>;
  const cur = styles.find((s) => s.id === styleId);
  return (
    <div className="space-y-8" data-testid="admin-bank">
      <UnverifiedCard styles={styles} rule={rule} onChange={saveRule} />
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <aside className="bg-card border border-border p-4 h-fit" data-testid="bank-style-list">
          <p className="font-osd text-[10px] tracking-[0.18em] text-muted-foreground mb-3 px-2">STYLES · {styles.length}</p>
          <div className="flex lg:flex-col gap-1 overflow-x-auto">
            {styles.map((s) => (
              <button key={s.id} onClick={() => setStyleId(s.id)} data-testid={`bank-style-${s.id}`} aria-pressed={s.id === styleId}
                className={`shrink-0 text-left px-3 py-2 text-xs border transition-colors ${s.id === styleId ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <span className="font-bold">{s.name}</span>
                <span className="block text-[10px] opacity-70">{s.active}/{s.total} actifs{s.unverified ? ` · ${s.unverified} non vérifiés` : ""}</span>
              </button>
            ))}
          </div>
        </aside>
        <div className="space-y-6 min-w-0">
          <section className="bg-card border border-border p-6 sm:p-8" data-testid="bank-clips-card">
            <div className="flex items-baseline justify-between gap-4 mb-4">
              <h2 className="font-display text-lg font-bold">{cur?.name || "—"}</h2>
              <span className="text-xs text-muted-foreground" data-testid="bank-clips-total">{clips.length}/{total} clips</span>
            </div>
            <div className="space-y-1.5">
              {clips.map((c, i) => <ClipRow key={c.id} c={c} first={i === 0} last={i === clips.length - 1} onPatch={patchClip} onRemove={removeClip} />)}
            </div>
            {clips.length < total && <button onClick={() => loadClips(true)} data-testid="bank-clips-more" className="mt-4 border border-border px-5 h-11 text-sm hover:border-foreground">Voir plus</button>}
          </section>
          <PexelsSearch styleId={styleId} onAdded={() => { loadClips(false); loadStyles(); }} />
        </div>
      </div>
      <Reports reports={reports} onClose={closeReport} />
    </div>
  );
}
